import React from "react";
import { Link } from "react-router-dom";
import { Cookie, ArrowLeft, ExternalLink, ShieldCheck } from "lucide-react";

const LAST_UPDATE = "27 avril 2026";

const COOKIE_TABLE = [
  {
    cat: "Essentiels",
    color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    rows: [
      { name: "madafrica_token", purpose: "Garder ta session connectée (JWT)", duration: "30 jours", domain: "voamada.com" },
      { name: "madafrica_lang", purpose: "Mémoriser ta langue (FR/MG)", duration: "1 an", domain: "voamada.com" },
    ],
  },
  {
    cat: "Préférences",
    color: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    rows: [
      { name: "vola_streak_seen", purpose: "Ne pas répéter le toast de connexion quotidienne", duration: "1 jour", domain: "voamada.com" },
    ],
  },
  {
    cat: "Publicité (Google AdSense)",
    color: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30",
    rows: [
      { name: "__gads / __gpi", purpose: "Limiter la fréquence des annonces, mesurer leur diffusion", duration: "13 mois", domain: ".google.com" },
      { name: "IDE / DSID / NID", purpose: "Personnalisation des annonces Google", duration: "13 mois", domain: ".doubleclick.net / .google.com" },
      { name: "test_cookie", purpose: "Vérifier que les cookies sont activés", duration: "Session", domain: ".doubleclick.net" },
    ],
  },
  {
    cat: "Réseaux sociaux intégrés",
    color: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    rows: [
      { name: "Cookies YouTube", purpose: "Lecture des vidéos intégrées (lazy-loaded)", duration: "Variable", domain: ".youtube.com" },
      { name: "Cookies TikTok / Instagram / Facebook", purpose: "Embeds lorsque tu consultes une vidéo intégrée", duration: "Variable", domain: ".tiktok.com / .instagram.com / .facebook.com" },
    ],
  },
];

export const CookiesPage = () => (
  <div className="px-4 md:px-12 py-10 max-w-5xl mx-auto pb-32" data-testid="cookies-page">
    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-6">
      <ArrowLeft size={14} /> Retour à l'accueil
    </Link>

    <header className="mb-10">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow text-xs font-bold uppercase tracking-widest mb-4">
        <Cookie size={11} /> Cookies
      </span>
      <h1 className="font-display text-4xl md:text-6xl font-black mb-3 tracking-tight">
        Politique des cookies
      </h1>
      <p className="text-white/60 max-w-3xl">
        Voamada utilise des cookies pour faire fonctionner le site, mémoriser tes préférences et financer
        la plateforme via la publicité. Voici la liste détaillée et comment les gérer.
      </p>
      <p className="text-xs text-white/40 mt-3">Dernière mise à jour : {LAST_UPDATE}</p>
    </header>

    <section className="mb-10">
      <h2 className="font-display text-2xl font-black mb-3">Qu'est-ce qu'un cookie ?</h2>
      <p className="text-white/75 leading-relaxed">
        Un cookie est un petit fichier texte stocké par ton navigateur quand tu visites un site. Il permet
        au site de te reconnaître à ta prochaine visite, de mémoriser tes préférences (langue, panier, session)
        ou de mesurer son audience. Certains cookies sont posés par Voamada directement, d'autres par des
        services tiers (Google AdSense, YouTube, etc.).
      </p>
    </section>

    <section className="mb-10 space-y-6">
      <h2 className="font-display text-2xl font-black">Liste des cookies utilisés</h2>
      {COOKIE_TABLE.map((cat) => (
        <div key={cat.cat} className="rounded-2xl border border-white/10 overflow-hidden">
          <div className={`px-4 py-3 border-b border-white/10 ${cat.color.split(" ").slice(0, 2).join(" ")}`}>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border ${cat.color}`}>
              {cat.cat}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/45 font-bold">
                  <th className="p-3">Nom</th>
                  <th className="p-3">Finalité</th>
                  <th className="p-3">Durée</th>
                  <th className="p-3">Domaine</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {cat.rows.map((r) => (
                  <tr key={r.name + r.domain}>
                    <td className="p-3 font-mono text-xs text-brand-orange">{r.name}</td>
                    <td className="p-3 text-white/80">{r.purpose}</td>
                    <td className="p-3 text-white/60">{r.duration}</td>
                    <td className="p-3 text-white/50 font-mono text-xs">{r.domain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </section>

    <section className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <h2 className="font-display text-2xl font-black mb-3 flex items-center gap-2">
        <ShieldCheck size={20} className="text-brand-orange" /> Comment gérer les cookies ?
      </h2>
      <ol className="list-decimal pl-5 space-y-3 text-white/75">
        <li>
          <strong>Bannière de consentement</strong> — au premier passage sur Voamada, tu peux accepter, refuser
          ou personnaliser tes choix. Tu peux modifier ces préférences à tout moment depuis le footer
          (lien "Gérer mes cookies").
        </li>
        <li>
          <strong>Paramètres de ton navigateur</strong> — chaque navigateur permet de bloquer ou supprimer
          les cookies. Voici les guides officiels :
          <ul className="list-disc pl-5 mt-1.5 space-y-0.5 text-sm">
            <li><a className="text-brand-orange hover:underline inline-flex items-center gap-1" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome <ExternalLink size={11} /></a></li>
            <li><a className="text-brand-orange hover:underline inline-flex items-center gap-1" href="https://support.mozilla.org/fr/kb/effacer-cookies" target="_blank" rel="noopener noreferrer">Firefox <ExternalLink size={11} /></a></li>
            <li><a className="text-brand-orange hover:underline inline-flex items-center gap-1" href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari <ExternalLink size={11} /></a></li>
            <li><a className="text-brand-orange hover:underline inline-flex items-center gap-1" href="https://support.microsoft.com/fr-fr/microsoft-edge" target="_blank" rel="noopener noreferrer">Edge <ExternalLink size={11} /></a></li>
          </ul>
        </li>
        <li>
          <strong>Refuser la publicité personnalisée Google</strong> — désactive le ciblage AdSense :
          <a className="text-brand-orange hover:underline ml-1" href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Paramètres Google Ads</a>
        </li>
        <li>
          <strong>Outils sectoriels</strong> :
          <a className="text-brand-orange hover:underline ml-1" href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">Youronlinechoices.com</a> (Europe) ·
          <a className="text-brand-orange hover:underline ml-1" href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Aboutads.info</a> (USA)
        </li>
      </ol>
      <p className="mt-4 text-sm text-white/55">
        ℹ️ Bloquer les cookies essentiels peut empêcher le bon fonctionnement de Voamada (impossibilité de te connecter, perte des préférences).
      </p>
    </section>

    <p className="text-sm text-white/55">
      Voir aussi : <Link to="/confidentialite" className="text-brand-orange hover:underline">Politique de confidentialité</Link> ·
      <Link to="/cgu" className="text-brand-orange hover:underline ml-1">CGU</Link> ·
      <Link to="/mentions-legales" className="text-brand-orange hover:underline ml-1">Mentions légales</Link>
    </p>
  </div>
);

export default CookiesPage;
